import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { adminAPI } from '../../services/adminService';
import PanchTatvaButton from '../../components/PanchTatvaButton';
import ShipmentBadge from '../../components/ShipmentBadge';
import RetryShipmentModal from '../../components/RetryShipmentModal';
import { CartToastContainer, showCartToast } from '../../components/CartToast';

export default function AdminOrders() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const s = strings.adminShipment || {};
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundData, setRefundData] = useState({ amount: '', reason: '', notes: '' });

  // ── New state — shipment UI ────────────────────────────────────────────────
  // Shipment status filter — filters by order.shiprocket.status
  const [shipmentFilter, setShipmentFilter] = useState('all');
  // Which order row is expanded to show the shipment detail panel
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  // Order targeted for retry modal
  const [retryOrder, setRetryOrder] = useState(null);
  // Retry in-flight guard: orderId → true (prevents duplicate clicks)
  const [retryingIds, setRetryingIds] = useState({});

  const ORDER_STATUSES = [
    'all',
    'pending_payment',
    'confirmed',
    'shipped',
    'delivered',
    'cancelled',
    'refunded'
  ];

  const REFUND_REASONS = [
    'customer_request',
    'item_damaged',
    'item_defective',
    'order_cancelled',
    'duplicate_order',
    'wrong_item_sent',
    'not_as_described',
    'return_processed'
  ];

  // Shipment filter options (new)
  const SHIPMENT_FILTERS = [
    { value: 'all', label: s.filterAll || 'All Shipments' },
    { value: 'processing', label: s.filterProcessing || 'Processing' },
    { value: 'failed_reconciling', label: s.filterRetrying || 'Retrying' },
    { value: 'permanent_failed', label: s.filterFailed || 'Failed' },
    { value: 'completed', label: s.filterShipped || 'Shipped' },
    { value: 'none', label: s.filterNotStarted || 'Not Started' },
  ];

  // Badge counter — permanent_failed orders visible on current page
  const failedCount = orders.filter((o) => o.shiprocket?.status === 'permanent_failed').length;


  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken();
      
      const params = new URLSearchParams({
        page,
        limit: 10,
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : '',
        // Pass shipment filter to backend if supported; client-side filter as fallback
        shipmentStatus: shipmentFilter !== 'all' && shipmentFilter !== 'none' ? shipmentFilter : '',
      });
      
      const response = await adminAPI.orders.list(params, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }
      
      const data = await response.json();
      setOrders(data.orders);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      setError(err.message);
      showCartToast(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [getIdToken, page, searchTerm, statusFilter, shipmentFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const idToken = await getIdToken();
      const response = await adminAPI.orders.update(orderId, { status: newStatus }, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      showCartToast('Order updated successfully!');
      fetchOrders();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  const handleRefund = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;

    try {
      const idToken = await getIdToken();
      const response = await adminAPI.orders.refund(selectedOrder._id, {
        amount: parseFloat(refundData.amount),
        reason: refundData.reason,
        notes: refundData.notes
      }, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to process refund');
      }

      showCartToast('Refund initiated successfully!');
      setShowRefundForm(false);
      setRefundData({ amount: '', reason: '', notes: '' });
      fetchOrders();
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  // ── New handlers — shipment ───────────────────────────────────────────────
  const toggleExpand = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const openRetryModal = (order) => {
    if (retryingIds[order.orderId]) return; // Guard: already in-flight
    setRetryOrder(order);
  };

  const handleRetryQueued = (_order) => {
    setRetryingIds((prev) => ({ ...prev, [_order.orderId]: true }));
    setRetryOrder(null);
    showCartToast(s.retryQueuedToast || 'Retry queued — refresh in a few seconds to see updated status.');
  };

  const handleShowFailedOnly = () => {
    setShipmentFilter('permanent_failed');
    setPage(1);
  };

  // Client-side "Not Started" filter fallback (backend may not support this param)
  const visibleOrders = shipmentFilter === 'none'
    ? orders.filter((o) => !o.shiprocket)
    : orders;

  // ── UI helpers ────────────────────────────────────────────────────────────
  const formatAwb = (order) => {
    const awb = order.shiprocket?.awb_code;
    const courier = order.shiprocket?.courier_name;
    if (!awb) return null;
    return courier ? `${courier} — ${awb}` : awb;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-green-600';
      case 'shipped': return 'bg-blue-600';
      case 'confirmed': return 'bg-purple-600';
      case 'pending_payment': return 'bg-yellow-600';
      case 'cancelled': return 'bg-red-600';
      case 'refunded': return 'bg-pink-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Back Button */}
        <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {strings.admin?.backToDashboard || 'Back to Dashboard'}
        </Link>

        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
              {strings.admin?.orders || 'Orders'}
            </h1>
            <p className="text-bodyText font-sans">
              {strings.admin?.manageAllOrders || 'View and manage all customer orders'}
            </p>
          </div>

          {/* Failed shipments badge counter — only shown when >0 on current page */}
          {failedCount > 0 && (
            <button
              onClick={handleShowFailedOnly}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-xl text-red-800 text-sm font-sans font-medium hover:bg-red-100 transition-colors"
            >
              <span className="inline-flex items-center justify-center w-5 h-5 bg-red-600 text-white rounded-full text-xs font-bold">
                {failedCount}
              </span>
              {s.failedShipmentsAlert || 'Failed Shipments — click to filter'}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={strings.admin?.searchOrders || 'Search by order ID, email...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText font-sans placeholder-textSecondary focus:outline-none focus:border-secondary"
            />
          </div>
          {/* Order status filter (existing) */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText font-sans focus:outline-none focus:border-secondary"
          >
            {ORDER_STATUSES.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Status' : status.replace('_', ' ').toUpperCase()}
              </option>
            ))}
          </select>
          {/* Shipment status filter (new) */}
          <select
            value={shipmentFilter}
            onChange={(e) => { setShipmentFilter(e.target.value); setPage(1); }}
            className="px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText font-sans focus:outline-none focus:border-secondary"
          >
            {SHIPMENT_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Orders Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-bodyText font-sans">{strings.admin?.loading || 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 font-sans">
            {error}
          </div>
        ) : visibleOrders.length === 0 ? (
          <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center border border-secondary/10">
            <p className="text-bodyText text-lg font-sans">{strings.admin?.noOrders || 'No orders found'}</p>
          </div>
        ) : (
          <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-secondary/20">
                  <tr>
                    {/* Expand toggle column */}
                    <th className="px-4 py-4 w-8" />
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.orderId || 'Order ID'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.customer || 'Customer'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.amount || 'Amount'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.status || 'Status'}</th>
                    {/* New shipment columns */}
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{s.shipmentStatusCol || 'Shipment'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{s.awbCol || 'AWB / Courier'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.date || 'Date'}</th>
                    <th className="px-4 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.actions || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.map((order) => (
                    <React.Fragment key={order._id}>
                      {/* Main row */}
                      <tr className="border-b border-secondary/10 hover:bg-surface transition-colors duration-200">
                        {/* Expand toggle */}
                        <td className="px-4 py-4">
                          <button
                            onClick={() => toggleExpand(order._id)}
                            className="text-textSecondary hover:text-secondary transition-colors"
                            title={expandedOrderId === order._id ? 'Collapse' : (s.expandTitle || 'View shipment details')}
                          >
                            <svg className={`w-4 h-4 transition-transform duration-200 ${expandedOrderId === order._id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </td>

                        <td className="px-4 py-4 font-medium text-headingText font-sans text-sm">{order.orderId}</td>

                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-headingText font-sans text-sm">{order.customer.name}</p>
                            <p className="text-xs text-textSecondary font-sans">{order.customer.email}</p>
                          </div>
                        </td>

                        <td className="px-4 py-4 font-medium text-headingText font-sans text-sm">
                          ₹{order.amounts.total.toLocaleString('en-IN')}
                        </td>

                        {/* Order status (existing) */}
                        <td className="px-4 py-4">
                          <select
                            value={order.status}
                            onChange={(e) => handleStatusChange(order._id, e.target.value)}
                            className={`px-3 py-1 rounded-lg text-xs font-sans font-medium text-white bg-blue-600 border-0 focus:outline-none cursor-pointer ${getStatusColor(order.status)}`}
                          >
                            {ORDER_STATUSES.filter(st => st !== 'all').map(status => (
                              <option key={status} value={status}>
                                {status.replace(/_/g, ' ').toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Shipment status badge (new) */}
                        <td className="px-4 py-4">
                          <ShipmentBadge status={order.shiprocket?.status} />
                        </td>

                        {/* AWB + Courier (new) */}
                        <td className="px-4 py-4">
                          {formatAwb(order) ? (
                            <span className="text-xs font-sans text-headingText">{formatAwb(order)}</span>
                          ) : (
                            <span className="text-xs font-sans text-textSecondary">{s.awbPending || 'Pending'}</span>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-bodyText font-sans">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>

                        {/* Actions: existing refund + new retry (permanent_failed only) */}
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setRefundData({ amount: order.amounts.total, reason: '', notes: '' });
                                setShowRefundForm(true);
                              }}
                              className="px-3 py-1 text-sm font-sans text-secondary hover:bg-secondary/10 rounded-lg transition-colors duration-200"
                            >
                              {strings.admin?.refund || 'Refund'}
                            </button>

                            {/* Retry — ONLY shown for permanent_failed. Guards prevent double-submit. */}
                            {order.shiprocket?.status === 'permanent_failed' && (
                              <button
                                onClick={() => openRetryModal(order)}
                                disabled={!!retryingIds[order.orderId]}
                                title={s.retryButtonTitle || 'Force-retry shipment creation via admin queue'}
                                className={`px-3 py-1 text-sm font-sans rounded-lg transition-colors duration-200 ${
                                  retryingIds[order.orderId]
                                    ? 'text-textSecondary cursor-not-allowed opacity-50'
                                    : 'text-red-700 hover:bg-red-50'
                                }`}
                              >
                                {retryingIds[order.orderId]
                                  ? (s.retryInFlight || 'Queued…')
                                  : (s.retryButton || 'Retry Shipment')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded shipment detail panel */}
                      {expandedOrderId === order._id && (
                        <tr className="bg-surface border-b border-secondary/10">
                          <td colSpan={9} className="px-8 py-5">
                            <ShipmentDetailPanel order={order} strings={s} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-surface border-t border-secondary/20 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-sans text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  ← {strings.admin?.previous || 'Previous'}
                </button>
                <span className="text-sm font-sans text-bodyText">
                  {strings.admin?.page || 'Page'} {page} {strings.admin?.of || 'of'} {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-sans text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  {strings.admin?.next || 'Next'} →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Refund Modal */}
        {showRefundForm && selectedOrder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-cardBg rounded-2xl shadow-xl p-8 max-w-md w-full border border-secondary/10">
              <h2 className="text-2xl font-heading text-headingText mb-6">
                {strings.admin?.refundOrder || 'Refund Order'}
              </h2>

              <form onSubmit={handleRefund} className="space-y-6">
                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.refundAmount || 'Refund Amount'} (₹) *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    max={selectedOrder.amounts.total}
                    value={refundData.amount}
                    onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
                  />
                  <p className="text-xs text-textSecondary mt-1">
                    {strings.admin?.maxRefund || 'Max'}: ₹{selectedOrder.amounts.total}
                  </p>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.reason || 'Reason'} *
                  </label>
                  <select
                    required
                    value={refundData.reason}
                    onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
                  >
                    <option value="">{strings.admin?.selectReason || 'Select reason'}</option>
                    {REFUND_REASONS.map(reason => (
                      <option key={reason} value={reason}>
                        {reason.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-headingText mb-2">
                    {strings.admin?.notes || 'Notes'}
                  </label>
                  <textarea
                    value={refundData.notes}
                    onChange={(e) => setRefundData({ ...refundData, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText focus:outline-none focus:border-secondary"
                    placeholder="Additional notes..."
                    rows="3"
                  />
                </div>

                {/* Buttons */}
                <div className="flex gap-4 pt-4">
                  <PanchTatvaButton variant="primary" size="large" type="submit" fullWidth>
                    {strings.admin?.processRefund || 'Process Refund'}
                  </PanchTatvaButton>
                  <PanchTatvaButton 
                    variant="outline" 
                    size="large" 
                    onClick={() => {
                      setShowRefundForm(false);
                      setSelectedOrder(null);
                    }}
                    type="button"
                    fullWidth
                  >
                    {strings.admin?.cancel || 'Cancel'}
                  </PanchTatvaButton>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Retry Shipment Modal (new) ────────────────────────────────────── */}
        {retryOrder && (
          <RetryShipmentModal
            order={retryOrder}
            getIdToken={getIdToken}
            onClose={() => setRetryOrder(null)}
            onRetryQueued={handleRetryQueued}
            strings={s}
          />
        )}

      <CartToastContainer />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ShipmentDetailPanel
// Expanded row content — shows full shiprocket subdocument fields.
// Handles missing/partial data gracefully (async system — fields may be null).
// ─────────────────────────────────────────────────────────────────────────────
function ShipmentDetailPanel({ order, strings: s }) {
  const sr = order.shiprocket;

  if (!sr) {
    return (
      <p className="text-sm font-sans text-textSecondary">
        {s.noShipmentData || 'No shipment data yet. Order may predate the async pipeline, or shipment creation is pending.'}
      </p>
    );
  }

  const rows = [
    { label: s.detailStatus || 'Status', value: <ShipmentBadge status={sr.status} showTooltip={false} /> },
    { label: s.detailShipmentId || 'Shipment ID', value: sr.shipment_id || '—' },
    { label: s.detailAwb || 'AWB Code', value: sr.awb_code || (s.awbPending || 'Pending') },
    { label: s.detailCourier || 'Courier', value: sr.courier_name || '—' },
    {
      label: s.detailTracking || 'Tracking URL',
      value: sr.tracking_url
        ? <a href={sr.tracking_url} target="_blank" rel="noopener noreferrer" className="text-secondary underline text-sm font-sans">{s.trackLink || 'Track Package'}</a>
        : '—',
    },
    { label: s.detailShippingStatus || 'Shipping Status', value: sr.shipping_status || '—' },
    {
      label: s.detailEdd || 'Est. Delivery',
      value: sr.estimated_delivery_date
        ? new Date(sr.estimated_delivery_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        : '—',
    },
  ];

  return (
    <div>
      <p className="text-xs font-sans font-semibold text-textSecondary uppercase tracking-wide mb-3">
        {s.detailSectionTitle || 'Shipment Details'}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
        {rows.map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs text-textSecondary font-sans mb-0.5">{label}</p>
            <div className="text-sm font-sans text-headingText">{value}</div>
          </div>
        ))}
      </div>
      {/* Async note when still in progress */}
      {(sr.status === 'processing' || sr.status === 'failed_reconciling') && (
        <p className="mt-3 text-xs text-textSecondary font-sans italic">
          {s.detailAsyncNote || '⚡ Shipment is being created asynchronously — data will update once the background worker completes.'}
        </p>
      )}
    </div>
  );
}
